import { API, WS_URL } from '../api.js';
import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import useBackButton from '../hooks/useBackButton';
import { MoreHorizontal, Pencil, Eye, EyeOff, Trash2, Pin, PinOff, SlidersHorizontal, Plus, X, Check, ArrowUpDown, Send } from 'lucide-react';
import Avatar from '../components/Avatar';
import CommentItem from '../components/CommentItem';
import styles from './CommunityTab.module.css';

/* ── Extract first line as title, rest as body ──────────────────── */
function splitContent(content) {
  if (!content) return { title: '', body: '' };
  const idx = content.indexOf('\n');
  if (idx > 0 && idx <= 140) {
    return { title: content.slice(0, idx).trim(), body: content.slice(idx + 1).trim() };
  }
  return { title: '', body: content };
}

function timeAgo(d) {
  if (!d) return null;
  const m = Math.floor((Date.now() - new Date(d)) / 60000);
  if (m < 1)   return 'just now';
  if (m < 60)  return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24)  return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(d).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
}

function postDateLabel(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const ReadMoreText = ({ text, maxLength = 160 }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  if (!text) return null;
  if (text.length <= maxLength) return <span style={{ whiteSpace: 'pre-wrap' }}>{text}</span>;
  return (
    <span style={{ whiteSpace: 'pre-wrap' }}>
      {isExpanded ? text : `${text.substring(0, maxLength).trim()}… `}
      <span onClick={e => { e.stopPropagation(); setIsExpanded(!isExpanded); }}
        style={{ fontWeight: 600, cursor: 'pointer', color: 'var(--accent)', fontSize: 13 }}>
        {isExpanded ? 'See less' : 'Read more'}
      </span>
    </span>
  );
};

function getRequiredLevelForPerk(levelConfigsJson, keyword, defaultLevel) {
  if (!levelConfigsJson) return defaultLevel;
  try {
    const decoded = JSON.parse(levelConfigsJson);
    if (Array.isArray(decoded)) {
      for (const lvlObj of decoded) {
        const perks = lvlObj.perks;
        if (Array.isArray(perks)) {
          for (const perk of perks) {
            if (perk.toLowerCase().includes(keyword.toLowerCase())) {
              return lvlObj.level || defaultLevel;
            }
          }
        }
      }
    }
  } catch (e) {
    console.error("Error parsing levelConfigs for perk", e);
  }
  return defaultLevel;
}

export default function CommunityTab({ onToast, community, activeMembership, searchQuery = '' }) {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightedPostId = searchParams.get('post');
  const [posts, setPosts] = useState([]);
  const [page,  setPage]  = useState(1);
  const [modal, setModal] = useState(false);
  const [draft, setDraft] = useState({ content: '' });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [images, setImages] = useState([]);
  const [video, setVideo] = useState(null);
  const [tagsInput, setTagsInput] = useState('');
  const [users, setUsers] = useState([]);
  const [optionsOpen, setOptionsOpen] = useState(null);
  const [editPostId, setEditPostId] = useState(null);
  const [gallery, setGallery] = useState({ open: false, post: null, currentIndex: 0 });
  const [comments, setComments] = useState({});
  const [commentInput, setCommentInput] = useState('');
  const [commentImage, setCommentImage] = useState(null);
  const [replyingTo, setReplyingTo] = useState(null);
  const [commentOptionsOpen, setCommentOptionsOpen] = useState(null);
  const [loadingComments, setLoadingComments] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const [settingsModal, setSettingsModal] = useState(false);
  const [settingsForm, setSettingsForm] = useState({
    name: community?.name || '',
    description: community?.description || '',
    note: community?.note || '',
    isPrivate: community?.isPrivate ?? community?.is_private ?? false,
    coverImageUrl: community?.coverImageUrl || '',
    aboutImages: community?.aboutImages || [],
  });
  const [savingSettings, setSavingSettings] = useState(false);

  // ── Categories & sort ──────────────────────────────────────────────────────
  const [categories,     setCategories]     = useState([]);
  const [activeCategory, setActiveCategory] = useState(null); // null = All
  const [sort,           setSort]           = useState('default');
  const [showSortMenu,   setShowSortMenu]   = useState(false);
  const [showCatModal,   setShowCatModal]   = useState(false);
  const [catForm,        setCatForm]        = useState({ name: '', emoji: '' });
  const [editingCat,     setEditingCat]     = useState(null); // category being edited
  const [savingCat,      setSavingCat]      = useState(false);

  useEffect(() => {
    if (community) {
      setSettingsForm({
        name: community.name || '',
        description: community.description || '',
        note: community.note || '',
        isPrivate: community.isPrivate ?? community.is_private ?? false,
        coverImageUrl: community.coverImageUrl || '',
        aboutImages: community.aboutImages || [],
      });
    }
  }, [community]);

  useBackButton(() => {
    if (gallery.open) { setGallery({ open: false, post: null, currentIndex: 0 }); }
    else if (settingsModal) { setSettingsModal(false); }
    else if (showCatModal) { setShowCatModal(false); }
    else if (modal) { setModal(false); }
  }, !!(gallery.open || settingsModal || showCatModal || modal));

  const wsRef = useRef(null);
  const fileInputRef = useRef(null);
  const commentFileInputRef = useRef(null);
  const coverFileInputRef = useRef(null);
  const aboutImagesInputRef = useRef(null);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setModal(false);
        setSettingsModal(false);
        setGallery({ open: false, post: null, currentIndex: 0 });
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    const commId = community?.id;
    if (!commId) return;
    setLoading(true);
    fetch(`${API}/api/community/posts?communityId=${commId}`)
      .then(res => res.json())
      .then(data => { if (Array.isArray(data)) setPosts(data); setLoading(false); })
      .catch(() => setLoading(false));
      
    if (!community?.id) return;
    fetch(`${API}/api/community/members?communityId=${community.id}`)
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) setUsers(data);
        else if (data.items) setUsers(data.items);
      })
      .catch(() => {});
  }, [community?.id]);

  // Posts are NOT real-time. The newest posts load on mount (page reload or
  // switching to the Community tab) and when the user scrolls back to the top.
  useEffect(() => {
    const scroller = document.querySelector('.qeduvo-main');
    if (!scroller || !community?.id) return;
    let t;
    const refresh = () => {
      fetch(`${API}/api/community/posts?communityId=${community.id}`)
        .then(r => r.json())
        .then(d => { if (Array.isArray(d)) setPosts(d); })
        .catch(() => {});
    };
    const onScroll = () => {
      if (scroller.scrollTop <= 40) { clearTimeout(t); t = setTimeout(refresh, 400); }
    };
    scroller.addEventListener('scroll', onScroll, { passive: true });
    return () => { scroller.removeEventListener('scroll', onScroll); clearTimeout(t); };
  }, [community?.id]);

  // Fetch categories when community changes
  useEffect(() => {
    if (!community?.id) return;
    fetch(`${API}/api/communities/${community.id}/categories`)
      .then(r => r.ok ? r.json() : [])
      .then(data => setCategories(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [community?.id]);

  // Open highlighted post from notification click (?post=123)
  useEffect(() => {
    if (highlightedPostId && posts.length > 0) {
      const p = posts.find(x => String(x.id) === String(highlightedPostId));
      if (p) {
        openGallery(p);
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('post');
        setSearchParams(newParams, { replace: true });
      } else {
        // Fetch from backend individually if not present in active posts page
        fetch(`${API}/api/community/posts?postId=${highlightedPostId}`)
          .then(res => res.json())
          .then(data => {
            if (Array.isArray(data) && data.length > 0) {
              const fetchedPost = data[0];
              openGallery(fetchedPost);
              const newParams = new URLSearchParams(searchParams);
              newParams.delete('post');
              setSearchParams(newParams, { replace: true });
            }
          })
          .catch(() => {});
      }
    }
  }, [highlightedPostId, posts, searchParams, setSearchParams]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (optionsOpen && !e.target.closest(`.${styles.postOptionsWrap}`)) {
        setOptionsOpen(null);
      }
      if (commentOptionsOpen && !e.target.closest(`.${styles.commentOptionsWrap}`)) {
        setCommentOptionsOpen(null);
      }
      if (showSortMenu && !e.target.closest(`.${styles.sortWrap}`)) {
        setShowSortMenu(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [optionsOpen, commentOptionsOpen, showSortMenu]);

  useEffect(() => {
    const ws = new WebSocket(WS_URL);
    wsRef.current = ws;
    ws.onopen = () => console.log('WS connected');
    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'connected') return;
        // Handle real-time updates for posts and comments
        if (data.eventType === 'new_comment' && data.payload) {
          const newComment = JSON.parse(data.payload);
          const eventPostId = Number(data.postId || newComment.postId);
          
          setComments(prev => {
            const currentComments = prev[eventPostId] || [];
            // If we already received/added this comment (e.g. from local submission), skip increment
            if (currentComments.some(c => Number(c.id) === Number(newComment.id))) {
              return prev;
            }
            
            // Otherwise increment post counts in real-time
            setPosts(postsPrev => postsPrev.map(p => Number(p.id) === eventPostId ? { ...p, commentsCount: (p.commentsCount || 0) + 1 } : p));
            setGallery(galleryPrev => {
              if (galleryPrev.open && galleryPrev.post && Number(galleryPrev.post.id) === eventPostId) {
                return { ...galleryPrev, post: { ...galleryPrev.post, commentsCount: (galleryPrev.post.commentsCount || 0) + 1 } };
              }
              return galleryPrev;
            });
            
            return { ...prev, [eventPostId]: [...currentComments, newComment] };
          });
        } else if (data.eventType === 'like' && data.payload) {
          const payload = JSON.parse(data.payload);
          const eventPostId = Number(data.postId || payload.postId);
          setPosts(prev => prev.map(p => Number(p.id) === eventPostId ? { ...p, likesCount: payload.likes } : p));
          setGallery(prev => {
            if (prev.open && prev.post && Number(prev.post.id) === eventPostId) {
              return { ...prev, post: { ...prev.post, likesCount: payload.likes } };
            }
            return prev;
          });
        } else if (data.eventType === 'update_comment' && data.payload) {
          const updated = JSON.parse(data.payload);
          const eventPostId = Number(data.postId || updated.postId);
          setComments(prev => ({
            ...prev,
            [eventPostId]: (prev[eventPostId] || []).map(c => Number(c.id) === Number(updated.id) ? updated : c)
          }));
        } else if (data.eventType === 'delete_comment' && data.payload) {
          const deleted = JSON.parse(data.payload);
          const eventPostId = Number(data.postId || deleted.postId);
          setComments(prev => ({
            ...prev,
            [eventPostId]: (prev[eventPostId] || []).map(c => Number(c.id) === Number(deleted.id) ? deleted : c)
          }));
        } else if ((data.eventType === 'like_comment' || data.eventType === 'dislike_comment') && data.payload) {
          const payload = JSON.parse(data.payload);
          const eventPostId = Number(data.postId || payload.postId);
          setComments(prev => ({
            ...prev,
            [eventPostId]: (prev[eventPostId] || []).map(c => Number(c.id) === Number(payload.commentId) ? { ...c, likesCount: payload.likes, dislikesCount: payload.dislikes } : c)
          }));
        }
      } catch (err) { console.error('WS parse error', err); }
    };
    ws.onclose = () => console.log('WS disconnected');
    return () => ws.close();
  }, [user?.id, onToast]);

  async function toggleLike(id) {
    if (!user) return onToast('Please login to like posts');
    setPosts(prev => prev.map(p => p.id === id ? { ...p, likesCount: (p.likesCount || 0) + 1, liked: true } : p));
    setGallery(prev => {
      if (prev.open && prev.post && Number(prev.post.id) === Number(id)) {
        return { ...prev, post: { ...prev.post, likesCount: (prev.post.likesCount || 0) + 1, liked: true } };
      }
      return prev;
    });
    try {
      const res = await fetch(`${API}/api/community/posts/${id}/like`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
    } catch {
      setPosts(prev => prev.map(p => p.id === id ? { ...p, likesCount: Math.max(0, (p.likesCount || 1) - 1), liked: false } : p));
      setGallery(prev => {
        if (prev.open && prev.post && Number(prev.post.id) === Number(id)) {
          return { ...prev, post: { ...prev.post, likesCount: Math.max(0, (prev.post.likesCount || 1) - 1), liked: false } };
        }
        return prev;
      });
    }
  }

  async function toggleSave(postId) {
    if (!user) return onToast('Please login to save posts');
    // Optimistic update
    setPosts(prev => prev.map(p => p.id === postId ? { ...p, userSaved: !p.userSaved } : p));
    try {
      const res = await fetch(`${API}/api/community/posts/${postId}/save`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setPosts(prev => prev.map(p => p.id === postId ? { ...p, userSaved: data.saved } : p));
        onToast(data.saved ? '🔖 Post saved to your pins' : 'Removed from saved posts');
      }
    } catch {
      // Revert on error
      setPosts(prev => prev.map(p => p.id === postId ? { ...p, userSaved: !p.userSaved } : p));
    }
  }

  useEffect(() => {
    if (!modal) {
      setImages([]);
      setVideo(null);
      setTagsInput('');
      setDraft({ content: '' });
      setEditPostId(null);
    }
  }, [modal]);

  const handleEdit = (post) => {
    setEditPostId(post.id);
    setDraft({ content: post.content, categoryId: post.categoryId || null });
    setTagsInput((post.tags || []).join(', '));
    setImages(post.imageUrls || []);
    setVideo(post.videoUrl ? { isExisting: true, url: post.videoUrl } : null);
    setOptionsOpen(null);
    setModal(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this post?')) return;
    try {
      await fetch(`${API}/api/community/posts/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      setPosts(prev => prev.filter(p => p.id !== id));
      onToast('Post deleted');
    } catch {
      onToast('Failed to delete post');
    }
  };

  const handleTogglePublish = async (post) => {
    try {
      const res = await fetch(`${API}/api/community/posts/${post.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ isPublished: !(post.isPublished ?? true) })
      });
      if (res.ok) {
        setPosts(prev => prev.map(p => p.id === post.id ? { ...p, isPublished: !(post.isPublished ?? true) } : p));
        onToast(`Post ${!(post.isPublished ?? true) ? 'published' : 'unpublished'}`);
      }
    } catch {
      onToast('Failed to update post status');
    }
    setOptionsOpen(null);
  };

  const handleTogglePin = async (post) => {
    try {
      const res = await fetch(`${API}/api/community/posts/${post.id}/pin`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setPosts(prev => prev.map(p => p.id === post.id ? { ...p, isPinned: data.isPinned } : p));
        onToast(data.isPinned ? '📌 Post pinned' : 'Post unpinned');
      } else {
        const err = await res.json();
        onToast(err.message || 'Failed to update pin');
      }
    } catch { onToast('Failed to update pin'); }
    setOptionsOpen(null);
  };

  // ── Category management ────────────────────────────────────────────────────
  async function saveCat() {
    if (!catForm.name.trim()) return;
    setSavingCat(true);
    try {
      if (editingCat) {
        const res = await fetch(`${API}/api/communities/${community.id}/categories/${editingCat.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ name: catForm.name.trim(), emoji: catForm.emoji }),
        });
        if (!res.ok) { const e = await res.json().catch(() => ({})); onToast(e.error || 'Failed to update category'); setSavingCat(false); return; }
        const updated = await res.json();
        setCategories(prev => prev.map(c => c.id === editingCat.id ? updated : c));
      } else {
        const res = await fetch(`${API}/api/communities/${community.id}/categories`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ name: catForm.name.trim(), emoji: catForm.emoji, sortOrder: categories.length }),
        });
        if (!res.ok) { const e = await res.json().catch(() => ({})); onToast(e.error || 'Failed to create category'); setSavingCat(false); return; }
        const created = await res.json();
        setCategories(prev => [...prev, created]);
      }
      setCatForm({ name: '', emoji: '' });
      setEditingCat(null);
    } catch { onToast('Failed to save category'); }
    setSavingCat(false);
  }

  async function deleteCat(cat) {
    if (!confirm(`Delete category "${cat.name}"? Posts in this category will become uncategorised.`)) return;
    try {
      await fetch(`${API}/api/communities/${community.id}/categories/${cat.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      setCategories(prev => prev.filter(c => c.id !== cat.id));
      if (activeCategory === cat.id) setActiveCategory(null);
    } catch { onToast('Failed to delete category'); }
  }

  async function submitPost() {
    if (!draft.content.trim() || !user) return;
    setUploading(true);

    let finalImageUrls = [];
    let finalVideoUrl = video && video.isExisting ? video.url : null;

    if (images.length > 0) {
      try {
        const promises = images.map(async (img) => {
          if (typeof img === 'string') return img; // existing url
          const ext = img.name.substring(img.name.lastIndexOf('.'));
          const res = await fetch(`${API}/api/upload`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'X-File-Ext': ext },
            body: img
          });
          if (!res.ok) throw new Error();
          const data = await res.json();
          return data.url;
        });
        finalImageUrls = await Promise.all(promises);
      } catch (err) {
        onToast('Image upload failed');
        setUploading(false);
        return;
      }
    } else if (video && !video.isExisting) {
      const ext = video.name.substring(video.name.lastIndexOf('.'));
      try {
        const res = await fetch(`${API}/api/upload`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'X-File-Ext': ext },
          body: video
        });
        if (!res.ok) throw new Error();
        const data = await res.json();
        finalVideoUrl = data.url;
      } catch (err) {
        onToast('Video upload failed');
        setUploading(false);
        return;
      }
    }

    const tags = tagsInput.split(',').map(t => t.trim().replace(/^#/, '')).filter(Boolean);
    const content = draft.content;
    
    try {
      const isEdit = !!editPostId;
      const url = isEdit ? `${API}/api/community/posts/${editPostId}` : `${API}/api/community/posts`;
      const method = isEdit ? 'PATCH' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ content, imageUrls: finalImageUrls, videoUrl: finalVideoUrl, tags, communityId: community?.id, categoryId: draft.categoryId ?? null }),
      });
      if (!res.ok) throw new Error();
      
      if (isEdit) {
        const updated = await res.json();
        if (updated.categoryId) {
          const cat = categories.find(c => Number(c.id) === Number(updated.categoryId));
          if (cat) {
            updated.categoryName = cat.name;
            updated.categoryEmoji = cat.emoji;
          }
        }
        setPosts(prev => prev.map(p => p.id === editPostId ? updated : p));
        onToast('✓ Post updated!');
      } else {
        const newPost = await res.json();
        if (newPost.categoryId) {
          const cat = categories.find(c => Number(c.id) === Number(newPost.categoryId));
          if (cat) {
            newPost.categoryName = cat.name;
            newPost.categoryEmoji = cat.emoji;
          }
        }
        setPosts(prev => [newPost, ...prev]);
        onToast('✓ Post published! +30 XP');
      }
      setModal(false);
    } catch { onToast(`Error ${editPostId ? 'updating' : 'publishing'} post`); }
    setUploading(false);
  }

  async function fetchComments(postId) {
    if (!postId) return;
    setLoadingComments(true);
    try {
      const res = await fetch(`${API}/api/community/posts/${postId}/comments`);
      if (res.ok) {
        const data = await res.json();
        setComments(prev => ({ ...prev, [postId]: data }));
      }
    } catch (e) {
      console.error('Failed to fetch comments', e);
    }
    setLoadingComments(false);
  }

  async function submitComment(postId) {
    if ((!commentInput.trim() && !commentImage) || !user) return;
    const content = commentInput.trim();
    setCommentInput('');
    
    let uploadedImageUrl = null;
    if (commentImage) {
      const ext = commentImage.name.substring(commentImage.name.lastIndexOf('.'));
      try {
        const upRes = await fetch(`${API}/api/upload`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'X-File-Ext': ext },
          body: commentImage
        });
        if (upRes.ok) {
          const upData = await upRes.json();
          uploadedImageUrl = upData.url;
        }
      } catch (e) {
        onToast('Failed to upload image');
        return;
      }
      setCommentImage(null);
    }
    
    try {
      const res = await fetch(`${API}/api/community/posts/${postId}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ content, imageUrl: uploadedImageUrl, parentId: replyingTo?.id }),
      });
      if (res.ok) {
        const newComment = await res.json();
        setComments(prev => {
          const currentComments = prev[postId] || [];
          if (currentComments.some(c => Number(c.id) === Number(newComment.id))) {
            return prev;
          }
          // Only increment post commentsCount if it hasn't already been added/processed
          setPosts(postsPrev => postsPrev.map(p => Number(p.id) === Number(postId) ? { ...p, commentsCount: (p.commentsCount || 0) + 1 } : p));
          setGallery(galleryPrev => {
            if (galleryPrev.open && galleryPrev.post && Number(galleryPrev.post.id) === Number(postId)) {
              return { ...galleryPrev, post: { ...galleryPrev.post, commentsCount: (galleryPrev.post.commentsCount || 0) + 1 } };
            }
            return galleryPrev;
          });
          return {
            ...prev,
            [postId]: [...currentComments, newComment]
          };
        });
        setReplyingTo(null);
      }
    } catch (e) {
      console.error('Failed to submit comment', e);
      onToast('Failed to post comment');
    }
  }

  async function handleLikeComment(commentId, postId) {
    if (!user) return onToast('Please login to like comments');
    setComments(prev => ({
      ...prev,
      [postId]: (prev[postId] || []).map(c => c.id === commentId ? { ...c, likesCount: (c.likesCount || 0) + 1 } : c)
    }));
    try {
      await fetch(`${API}/api/community/comments/${commentId}/like`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
    } catch { }
  }

  async function handleDislikeComment(commentId, postId) {
    if (!user) return onToast('Please login to dislike comments');
    setComments(prev => ({
      ...prev,
      [postId]: (prev[postId] || []).map(c => c.id === commentId ? { ...c, dislikesCount: (c.dislikesCount || 0) + 1 } : c)
    }));
    try {
      await fetch(`${API}/api/community/comments/${commentId}/dislike`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
    } catch { }
  }

  async function handleEditComment(commentId, newContent) {
    try {
      const res = await fetch(`${API}/api/community/comments/${commentId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ content: newContent })
      });
      if (res.ok) {
        const updated = await res.json();
        setComments(prev => ({
          ...prev,
          [updated.postId]: (prev[updated.postId] || []).map(c => c.id === commentId ? updated : c)
        }));
      }
    } catch {
      onToast('Failed to edit comment');
    }
  }

  async function handleDeleteComment(commentId) {
    if (!confirm('Are you sure you want to delete this comment?')) return;
    try {
      const res = await fetch(`${API}/api/community/comments/${commentId}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const deleted = await res.json();
        setComments(prev => ({
          ...prev,
          [deleted.postId]: (prev[deleted.postId] || []).map(c => c.id === commentId ? deleted : c)
        }));
      }
    } catch {
      onToast('Failed to delete comment');
    }
  }

  async function handleTogglePublishComment(comment) {
    try {
      const res = await fetch(`${API}/api/community/comments/${comment.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ isPublished: !(comment.isPublished ?? true) })
      });
      if (res.ok) {
        const updated = await res.json();
        setComments(prev => ({
          ...prev,
          [updated.postId]: (prev[updated.postId] || []).map(c => c.id === comment.id ? updated : c)
        }));
      }
    } catch {
      onToast('Failed to update comment status');
    }
  }

  function openGallery(post, index = 0) {
    setGallery({ open: true, post, currentIndex: index });
    setReplyingTo(null);
    fetchComments(post.id);
  }

  const buildCommentTree = (commentsList) => {
    const map = {};
    const roots = [];
    commentsList.forEach(c => map[c.id] = { ...c, children: [] });
    commentsList.forEach(c => {
      if (c.parentId && map[c.parentId]) {
        map[c.parentId].children.push(map[c.id]);
      } else {
        roots.push(map[c.id]);
      }
    });
    return roots;
  };

  const renderComments = (nodes, level = 0) => {
    return nodes.map(node => (
      <CommentItem 
        key={node.id} 
        comment={node} 
        level={level}
        onReply={(c) => setReplyingTo({ id: c.id, name: c.authorName })}
        onLike={handleLikeComment}
        onDislike={handleDislikeComment}
        onEdit={handleEditComment}
        onDelete={handleDeleteComment}
        onTogglePublish={handleTogglePublishComment}
        currentUserId={user?.id}
        optionsOpen={commentOptionsOpen}
        setOptionsOpen={setCommentOptionsOpen}
      >
        {node.children.length > 0 && level < 3 && renderComments(node.children, level + 1)}
      </CommentItem>
    ));
  };

  function handlePostAction(post, action) {
    if (action === 'delete') handleDelete(post.id);
    else if (action === 'publish' || action === 'unpublish') handleTogglePublish(post);
  }

  function handleFileChange(e) {
    const selectedFiles = Array.from(e.target.files);
    if (selectedFiles.length === 0) return;
    
    if (selectedFiles[0].type.startsWith('video/')) {
      setVideo(selectedFiles[0]);
      setImages([]);
    } else {
      setImages(prev => [...prev, ...selectedFiles.filter(f => f.type.startsWith('image/'))].slice(0, 10));
      setVideo(null);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  async function saveCommunitySettings() {
    if (!settingsForm.name.trim()) return;
    setSavingSettings(true);
    try {
      const res = await fetch(`${API}/api/communities/${community.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(settingsForm),
      });
      if (res.ok) {
        onToast('✓ Community settings updated!');
        setSettingsModal(false);
        window.location.reload();
      } else {
        onToast('Failed to update community settings');
      }
    } catch {
      onToast('Error updating settings');
    }
    setSavingSettings(false);
  }

  async function handleCoverUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.substring(file.name.lastIndexOf('.'));
    try {
      onToast('Uploading cover picture...');
      const res = await fetch(`${API}/api/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'X-File-Ext': ext },
        body: file
      });
      if (res.ok) {
        const data = await res.json();
        setSettingsForm(prev => ({ ...prev, coverImageUrl: data.url }));
        onToast('✓ Cover picture uploaded!');
      } else {
        onToast('Failed to upload cover picture');
      }
    } catch {
      onToast('Error uploading cover picture');
    }
  }

  async function handleAboutImagesUpload(e) {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    onToast('Uploading attachment images...');
    try {
      const promises = files.map(async (file) => {
        const ext = file.name.substring(file.name.lastIndexOf('.'));
        const res = await fetch(`${API}/api/upload`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'X-File-Ext': ext },
          body: file
        });
        if (!res.ok) throw new Error();
        const data = await res.json();
        return data.url;
      });
      const urls = await Promise.all(promises);
      setSettingsForm(prev => ({
        ...prev,
        aboutImages: [...(prev.aboutImages || []), ...urls]
      }));
      onToast('✓ Attachment images uploaded!');
    } catch {
      onToast('Failed to upload some images');
    }
  }

  const timeAgo = (dateStr) => {
    const diff = Date.now() - new Date(dateStr).getTime();
    const min = Math.floor(diff / 60000);
    if (min < 1) return 'just now';
    if (min < 60) return `${min}m ago`;
    const h = Math.floor(min / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  };

  const isOwner = Number(user?.id) === Number(community?.ownerId) || user?.role === 'admin';
  const isCommAdmin = isOwner || activeMembership?.role === 'admin';
  const pinRequiredLevel = getRequiredLevelForPerk(community?.levelConfigs, 'pin', 5);
  const userLevel = activeMembership?.level ?? 1;
  const hasPinUnlocked = isCommAdmin || userLevel >= pinRequiredLevel;

  const displayedPosts = useMemo(() => {
    let list = activeCategory ? posts.filter(p => p.categoryId === activeCategory) : [...posts];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(p => {
        const content = (p.content || '').toLowerCase();
        const author = (p.authorName || '').toLowerCase();
        const tags = (p.tags || []).map(t => t.toLowerCase()).join(' ');
        return content.includes(q) || author.includes(q) || tags.includes(q);
      });
    }
    if (sort === 'new') list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    else if (sort === 'top') list.sort((a, b) => (b.likesCount || 0) - (a.likesCount || 0));
    else list.sort((a, b) => (b.isPinned ? 1 : 0) - (a.isPinned ? 1 : 0) || new Date(b.createdAt) - new Date(a.createdAt));
    return list;
  }, [posts, activeCategory, sort, searchQuery]);

  const POSTS_PER_PAGE = 10;

  // Reset to page 1 only when the FILTER changes (not on data refresh)
  // Using activeCategory/sort/searchQuery avoids resetting when goToPage()
  // triggers a scroll → scroll-listener → setPosts() → displayedPosts ref change
  useEffect(() => { setPage(1); }, [activeCategory, sort, searchQuery]);

  const totalPages  = Math.max(1, Math.ceil(displayedPosts.length / POSTS_PER_PAGE));
  const pagedPosts  = displayedPosts.slice((page - 1) * POSTS_PER_PAGE, page * POSTS_PER_PAGE);

  function goToPage(p) {
    setPage(p);
    // Scroll the main column back to top smoothly
    const el = document.querySelector('.qeduvo-main');
    if (el) el.scrollTo({ top: 0, behavior: 'smooth' });
  }

  // When showing "All", build a flat stream of { type:'header'|'post', ... } items
  const feedItems = useMemo(() => {
    const sortPosts = (arr) => {
      if (sort === 'top') return [...arr].sort((a, b) => (b.likesCount || 0) - (a.likesCount || 0));
      return [...arr].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    };

    if (activeCategory !== null) {
      return displayedPosts.map(p => ({ type: 'post', post: p }));
    }

    const items = [];
    const catIds = new Set(categories.map(c => c.id));

    // Pinned section
    const pinned = sortPosts(posts.filter(p => p.isPinned));
    if (pinned.length > 0) {
      items.push({ type: 'header', id: 'pinned', emoji: '📌', label: 'Pinned', count: pinned.length });
      pinned.forEach(p => items.push({ type: 'post', post: p }));
    }

    // Per-category sections
    categories.forEach(cat => {
      const catPosts = sortPosts(posts.filter(p => !p.isPinned && p.categoryId === cat.id));
      if (catPosts.length === 0) return;
      items.push({ type: 'header', id: `cat_${cat.id}`, emoji: cat.emoji, label: cat.name, count: catPosts.length });
      catPosts.forEach(p => items.push({ type: 'post', post: p }));
    });

    // Uncategorized / General
    const general = sortPosts(posts.filter(p => !p.isPinned && (!p.categoryId || !catIds.has(p.categoryId))));
    if (general.length > 0) {
      items.push({ type: 'header', id: 'general', emoji: '💬', label: 'General', count: general.length });
      general.forEach(p => items.push({ type: 'post', post: p }));
    }

    return items;
  }, [posts, categories, activeCategory, sort, displayedPosts]);

  if (loading) return <div className={styles.loading}>Loading community feed…</div>;

  const topUsers      = [...users].sort((a, b) => b.totalXp - a.totalXp).slice(0, 5);
  const adminsCount   = users.filter(u => u.role === 'admin').length;
  const membersCount  = users.length;
  const onlineCount   = users.filter(u => u.isOnline).length;
  const recentPosts = [...posts].slice(0, 3);

  return (
    <div className={styles.layout}>
      <div className={styles.mainColumn}>

        {/* Composer */}
        {user && (
          <div style={{ background: 'var(--surface)', borderRadius: 12, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', marginBottom: 20, border: '1px solid var(--border)' }} onClick={() => { setDraft({ content: '', categoryId: activeCategory || null }); setModal(true); }}>
            <Avatar user={{ avatar: user.avatar_url || user.avatarEmoji, name: user.display_name }} size={36} />
            <div style={{ color: 'var(--text3)', fontSize: 15 }}>Write something...</div>
          </div>
        )}

        {/* ── Category pills + Sort ── */}
        <div className={styles.categoryBar}>
          <div className={styles.categoryPills}>
            <button
              className={`${styles.pill} ${activeCategory === null ? styles.pillActive : ''}`}
              onClick={() => setActiveCategory(null)}
            >All</button>
            {categories.map(cat => (
              <button
                key={cat.id}
                className={`${styles.pill} ${activeCategory === cat.id ? styles.pillActive : ''}`}
                onClick={() => setActiveCategory(cat.id === activeCategory ? null : cat.id)}
              >
                {cat.emoji && <span>{cat.emoji}</span>}
                {cat.name}
              </button>
            ))}
            {isOwner && (
              <button className={styles.pillManage} onClick={() => setShowCatModal(true)} title="Manage categories">
                <SlidersHorizontal size={14} />
              </button>
            )}
          </div>

          <div className={styles.sortWrap}>
            <button className={styles.sortBtn} onClick={() => setShowSortMenu(v => !v)}>
              <ArrowUpDown size={15} />
              <span>{{ default: 'Default', new: 'New', top: 'Top' }[sort]}</span>
            </button>
            {showSortMenu && (
              <div className={styles.sortMenu}>
                {[['default','Default'],['new','New'],['top','Top']].map(([key, label]) => (
                  <button key={key} className={sort === key ? styles.sortItemActive : ''} onClick={() => { setSort(key); setShowSortMenu(false); }}>
                    {sort === key && <Check size={13} />}
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Category management modal ── */}
        {showCatModal && (
          <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setShowCatModal(false)}>
            <div className="modal" style={{ maxWidth: 480, borderRadius: 14 }}>
              <div className="modal-body" style={{ padding: 24 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                  <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800 }}>Manage Categories</h3>
                  <button onClick={() => { setShowCatModal(false); setEditingCat(null); setCatForm({ name: '', emoji: '' }); }}
                    style={{ background: 'var(--surface2)', border: 'none', borderRadius: '50%', width: 30, height: 30, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text2)' }}>
                    <X size={16} />
                  </button>
                </div>

                {/* Existing categories */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
                  {categories.length === 0 && <p style={{ color: 'var(--text3)', fontSize: 13, textAlign: 'center', margin: '12px 0' }}>No categories yet.</p>}
                  {categories.map(cat => (
                    <div key={cat.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 10, background: 'var(--surface2)', border: '1px solid var(--border)' }}>
                      <span style={{ fontSize: 18, minWidth: 24 }}>{cat.emoji}</span>
                      <span style={{ flex: 1, fontSize: 14, fontWeight: 600 }}>{cat.name}</span>
                      <button onClick={() => { setEditingCat(cat); setCatForm({ name: cat.name, emoji: cat.emoji }); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent)', padding: '4px 6px', borderRadius: 6 }}>
                        <Pencil size={14} />
                      </button>
                      <button onClick={() => deleteCat(cat)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: '4px 6px', borderRadius: 6 }}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>

                {/* Add / edit form */}
                <div style={{ background: 'var(--surface2)', borderRadius: 12, padding: 16, border: '1px solid var(--border)' }}>
                  <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12, color: 'var(--text2)' }}>
                    {editingCat ? `Edit "${editingCat.name}"` : 'Add category'}
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                    <input value={catForm.emoji} onChange={e => setCatForm(f => ({ ...f, emoji: e.target.value }))}
                      placeholder="🔥" maxLength={4}
                      style={{ width: 52, textAlign: 'center', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 6px', background: 'var(--surface)', color: 'var(--text)', fontSize: 18 }} />
                    <input value={catForm.name} onChange={e => setCatForm(f => ({ ...f, name: e.target.value }))}
                      placeholder="Category name"
                      style={{ flex: 1, border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', background: 'var(--surface)', color: 'var(--text)', fontSize: 14, outline: 'none' }} />
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {editingCat && (
                      <button onClick={() => { setEditingCat(null); setCatForm({ name: '', emoji: '' }); }}
                        style={{ flex: 1, padding: '9px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text2)', cursor: 'pointer', fontSize: 13 }}>
                        Cancel
                      </button>
                    )}
                    <button onClick={saveCat} disabled={!catForm.name.trim() || savingCat}
                      style={{ flex: 1, padding: '9px', borderRadius: 8, border: 'none', background: 'var(--accent)', color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: 13, opacity: !catForm.name.trim() ? 0.5 : 1 }}>
                      {savingCat ? 'Saving…' : editingCat ? 'Save changes' : 'Add'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Saved Posts Section ───────────────────────────────────────── */}
        {user && posts.some(p => p.userSaved) && (
          <div className={styles.savedSection}>
            <button
              className={styles.savedToggle}
              onClick={() => setShowSaved(v => !v)}
            >
              <span>🔖</span>
              <span>Your Saved Posts ({posts.filter(p => p.userSaved).length})</span>
              <span className={styles.savedChevron}>{showSaved ? '▲' : '▼'}</span>
            </button>
            {showSaved && (
              <div className={styles.savedList}>
                {posts.filter(p => p.userSaved).map(p => (
                  <div key={p.id} className={styles.savedItem} onClick={() => openGallery(p)}>
                    <div className={styles.savedItemText}>{p.content?.slice(0, 80) || 'Post'}{p.content?.length > 80 ? '…' : ''}</div>
                    <button
                      className={styles.savedItemRemove}
                      onClick={e => { e.stopPropagation(); toggleSave(p.id); }}
                      title="Remove from saved"
                    >✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Feed */}
        {displayedPosts.length === 0 && (
          <div className={styles.emptyState}>
            <div style={{ fontSize: 40 }}>💬</div>
            <p>{searchQuery.trim() ? "No posts found matching your search." : (activeCategory ? 'No posts in this category yet.' : 'No posts yet. Be the first to share!')}</p>
          </div>
        )}
      {pagedPosts.map(post => {
        const level = post.authorLevel || 1;
        const { title, body } = splitContent(post.content);
        const hasMedia = post.imageUrls?.length > 0 || post.videoUrl;
        const commentCount = post.commentsCount || 0;
        const lastCmt = timeAgo(post.lastCommentAt);

        return (
          <div
            className={`${styles.post} ${post.isPinned ? styles.postPinned : ''}`}
            key={post.id}
          >
            {/* ── Pinned badge — absolute top-right ── */}
            {post.isPinned && (
              <div className={styles.pinnedBadge}>
                <Pin size={12} /> Pinned
              </div>
            )}

            {/* ── Header: avatar · name · date · category ── */}
            <div className={styles.postHeader}>
              {/* Avatar with level badge */}
              <div className={styles.postAvatarWrap}>
                <Avatar user={{ avatar: post.authorAvatar, name: post.authorName }} size={44} />
                <div className={styles.postLevelBadge}>{level}</div>
              </div>

              <div className={styles.postMeta}>
                <div className={styles.authorName}>{post.authorName}</div>
                <div className={styles.postTime}>
                  {postDateLabel(post.createdAt)}
                  {post.categoryName && (
                    <span className={styles.categoryInline}>
                      · {post.categoryEmoji && <span>{post.categoryEmoji} </span>}{post.categoryName}
                    </span>
                  )}
                </div>
              </div>

              {/* Options menu */}
              {(Number(post.authorId) === Number(user?.id) || isCommAdmin) && (
                <div className={styles.postOptionsWrap}>
                  <button
                    className={styles.postOptionsBtn}
                    onClick={() => setOptionsOpen(optionsOpen === post.id ? null : post.id)}
                    title="More options"
                  ><MoreHorizontal size={18} /></button>
                  {optionsOpen === post.id && (
                    <div className={styles.postOptionsMenu}>
                      <button onClick={() => handleEdit(post)}>
                        <Pencil size={15} style={{ color: 'var(--accent)' }} /> Edit Post
                      </button>
                      <button onClick={() => {
                        if (!hasPinUnlocked) { onToast(`📌 Reach Level ${pinRequiredLevel} to pin posts`); return; }
                        handleTogglePin(post);
                      }}>
                        {post.isPinned
                          ? <><PinOff size={15} style={{ color: 'var(--text3)' }} />Unpin</>
                          : <><Pin size={15} style={{ color: hasPinUnlocked ? '#f59e0b' : 'var(--text3)' }} />{hasPinUnlocked ? 'Pin post' : `Pin (Lv.${pinRequiredLevel} 🔒)`}</>}
                      </button>
                      <button onClick={() => handlePostAction(post, post.isPublished ? 'unpublish' : 'publish')}>
                        {post.isPublished
                          ? <><EyeOff size={15} style={{ color: 'var(--text3)' }} />Unpublish</>
                          : <><Eye   size={15} style={{ color: '#10b981' }} />Publish</>}
                      </button>
                      <button onClick={() => handlePostAction(post, 'delete')} className={styles.postOptionsDelete}>
                        <Trash2 size={15} /> Delete
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* ── Body: content left · thumbnail right ── */}
            <div className={styles.postBody}>
              <div className={styles.postLeft}>
                {/* Category dot + title */}
                {title ? (
                  <>
                    <div className={styles.postTitle}>
                      {post.categoryName && (
                        <span className={styles.categoryDot} style={{ background: 'var(--accent)' }} />
                      )}
                      {title}
                    </div>
                    <div className={styles.postText}>
                      <ReadMoreText text={body} maxLength={160} />
                    </div>
                  </>
                ) : (
                  <div className={styles.postText}>
                    <ReadMoreText text={body} maxLength={200} />
                  </div>
                )}

                {post.tags?.length > 0 && (
                  <div className={styles.postTagsRow}>
                    {post.tags.map(t => <span key={t} className={styles.tag}>#{t}</span>)}
                  </div>
                )}

                {/* ── Footer ── */}
                <div className={styles.postFooter}>
                  {/* Like */}
                  <button
                    className={`${styles.footerBtn} ${post.liked ? styles.footerBtnLiked : ''}`}
                    onClick={() => toggleLike(post.id)}
                  >
                    <span className={styles.footerIcon}>👍</span>
                    <span className={styles.footerCount}>{post.likesCount || 0}</span>
                  </button>

                  {/* Comment */}
                  <button className={styles.footerBtn} onClick={() => openGallery(post)}>
                    <span className={styles.footerIcon}>💬</span>
                    <span className={styles.footerCount}>{commentCount}</span>
                  </button>

                  {/* Avatar stack + last comment */}
                  {commentCount > 0 && (
                    <div className={styles.commentRow}>
                      <div className={styles.commentorStack}>
                        {(post.recentCommenters || []).slice(0, 5).map((c, i) => {
                          const isImg = c.avatar && (c.avatar.startsWith('/') || c.avatar.startsWith('http'));
                          const initial = (c.name || '?')[0].toUpperCase();
                          const COLORS = ['#6366f1','#10b981','#f59e0b','#ef4444','#8b5cf6'];
                          const bg = COLORS[(c.name || '').charCodeAt(0) % COLORS.length];
                          return (
                            <div
                              key={c.id ?? i}
                              className={styles.stackAvatar}
                              style={{ zIndex: 5 - i, background: isImg ? 'transparent' : bg }}
                              title={c.name}
                            >
                              {isImg
                                ? <img src={`${API}${c.avatar}`} alt={c.name} style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
                                : <span style={{ fontSize: 10, fontWeight: 700, color: '#fff' }}>{initial}</span>
                              }
                            </div>
                          );
                        })}
                      </div>
                      {lastCmt && (
                        <span className={styles.lastComment}>New comment {lastCmt}</span>
                      )}
                    </div>
                  )}

                  {/* Save */}
                  <button
                    className={`${styles.footerBtn} ${post.userSaved ? styles.footerBtnSaved : ''}`}
                    style={{ marginLeft: 'auto' }}
                    onClick={() => toggleSave(post.id)}
                    title={post.userSaved ? 'Remove from saved' : 'Save'}
                  >
                    <span className={styles.footerIcon}>{post.userSaved ? '🔖' : '🔖'}</span>
                    <span className={styles.footerCount} style={{ fontSize: 11 }}>{post.userSaved ? 'Saved' : 'Save'}</span>
                  </button>
                </div>
              </div>

              {/* Thumbnail */}
              {hasMedia && (
                <div className={styles.postThumb} onClick={() => openGallery(post, 0)}>
                  {post.videoUrl
                    ? <video src={`${API}${post.videoUrl}`} className={styles.postThumbMedia} muted playsInline />
                    : <img src={`${API}${post.imageUrls[0]}`} alt="" className={styles.postThumbMedia} loading="lazy" />
                  }
                  {post.imageUrls?.length > 1 && (
                    <div className={styles.postThumbCount}>+{post.imageUrls.length - 1}</div>
                  )}
                  {post.videoUrl && <div className={styles.postThumbPlay}>▶</div>}
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* ── Modern Paginator ── */}
      {totalPages > 1 && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: 6, padding: '28px 0 12px', flexWrap: 'wrap',
        }}>
          {/* First */}
          <button
            onClick={() => goToPage(1)} disabled={page === 1}
            title="First page"
            style={{
              width: 36, height: 36, borderRadius: 10, border: '1px solid var(--border)',
              background: page === 1 ? 'var(--surface2)' : 'var(--surface)',
              color: page === 1 ? 'var(--text3)' : 'var(--text)',
              cursor: page === 1 ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 14, fontWeight: 700, transition: 'all 0.15s',
            }}
          >«</button>

          {/* Prev */}
          <button
            onClick={() => goToPage(page - 1)} disabled={page === 1}
            title="Previous"
            style={{
              width: 36, height: 36, borderRadius: 10, border: '1px solid var(--border)',
              background: page === 1 ? 'var(--surface2)' : 'var(--surface)',
              color: page === 1 ? 'var(--text3)' : 'var(--text)',
              cursor: page === 1 ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 14, fontWeight: 700, transition: 'all 0.15s',
            }}
          >‹</button>

          {/* Page numbers with ellipsis */}
          {(() => {
            const pages = [];
            const delta = 2;
            const left  = Math.max(2, page - delta);
            const right = Math.min(totalPages - 1, page + delta);

            pages.push(1);
            if (left > 2) pages.push('...');
            for (let i = left; i <= right; i++) pages.push(i);
            if (right < totalPages - 1) pages.push('...');
            if (totalPages > 1) pages.push(totalPages);

            return pages.map((p, idx) =>
              p === '...' ? (
                <span key={`ellipsis-${idx}`} style={{ width: 36, textAlign: 'center', color: 'var(--text3)', fontSize: 14 }}>…</span>
              ) : (
                <button
                  key={p}
                  onClick={() => goToPage(p)}
                  style={{
                    width: 36, height: 36, borderRadius: 10,
                    border: p === page ? 'none' : '1px solid var(--border)',
                    background: p === page ? 'var(--accent)' : 'var(--surface)',
                    color: p === page ? '#fff' : 'var(--text)',
                    cursor: 'pointer', fontWeight: p === page ? 800 : 500,
                    fontSize: 14, transition: 'all 0.15s',
                    boxShadow: p === page ? '0 4px 14px rgba(99,91,255,0.35)' : 'none',
                    transform: p === page ? 'scale(1.1)' : 'scale(1)',
                  }}
                >{p}</button>
              )
            );
          })()}

          {/* Next */}
          <button
            onClick={() => goToPage(page + 1)} disabled={page === totalPages}
            title="Next"
            style={{
              width: 36, height: 36, borderRadius: 10, border: '1px solid var(--border)',
              background: page === totalPages ? 'var(--surface2)' : 'var(--surface)',
              color: page === totalPages ? 'var(--text3)' : 'var(--text)',
              cursor: page === totalPages ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 14, fontWeight: 700, transition: 'all 0.15s',
            }}
          >›</button>

          {/* Last */}
          <button
            onClick={() => goToPage(totalPages)} disabled={page === totalPages}
            title="Last page"
            style={{
              width: 36, height: 36, borderRadius: 10, border: '1px solid var(--border)',
              background: page === totalPages ? 'var(--surface2)' : 'var(--surface)',
              color: page === totalPages ? 'var(--text3)' : 'var(--text)',
              cursor: page === totalPages ? 'not-allowed' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 14, fontWeight: 700, transition: 'all 0.15s',
            }}
          >»</button>

          {/* Page info */}
          <span style={{ fontSize: 12, color: 'var(--text3)', marginLeft: 8, whiteSpace: 'nowrap' }}>
            Page {page} of {totalPages} · {displayedPosts.length} posts
          </span>
        </div>
      )}

      {/* Create Post Modal */}
      {modal && user && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal" style={{ maxWidth: 650, borderRadius: 16, maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative' }}>
            
            {/* Modal Header: User info & Close button */}
            <div style={{ padding: '20px 24px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Avatar user={{ avatar: user.avatar_url, name: user.display_name }} size={34} />
                <div style={{ fontSize: 13, color: 'var(--text)' }}>
                  <span style={{ fontWeight: 700 }}>{user.display_name}</span>
                  <span className="text-muted"> posting in </span>
                  <span style={{ fontWeight: 700 }}>Community</span>
                </div>
              </div>
              <button
                onClick={() => setModal(false)}
                style={{
                  width: 32, height: 32, borderRadius: '50%', border: 'none',
                  background: 'var(--surface2)', color: 'var(--text2)',
                  cursor: 'pointer', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', fontSize: 16, lineHeight: 1,
                }}
              >✕</button>
            </div>

            {/* Scrollable Content Body */}
            <div style={{ padding: '20px 24px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>
              <input
                placeholder="Title"
                value={draft.title || ''}
                onChange={e => setDraft({ ...draft, title: e.target.value })}
                style={{ width: '100%', border: 'none', outline: 'none', background: 'transparent', color: 'var(--text)', fontSize: 22, fontWeight: 800 }}
              />

              {/* Category picker */}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => setDraft(d => ({ ...d, categoryId: null }))}
                  style={{
                    padding: '5px 14px', borderRadius: 20, cursor: 'pointer',
                    border: `1.5px solid ${!draft.categoryId ? 'var(--accent)' : 'var(--border)'}`,
                    background: !draft.categoryId ? 'var(--accent-soft)' : 'transparent',
                    color: !draft.categoryId ? 'var(--accent)' : 'var(--text3)',
                    fontSize: 13, fontWeight: 600, transition: 'all 0.15s',
                  }}>
                  No category
                </button>
                {categories.map(cat => (
                  <button
                    type="button"
                    key={cat.id}
                    onClick={() => setDraft(d => ({ ...d, categoryId: cat.id }))}
                    style={{
                      padding: '5px 14px', borderRadius: 20, cursor: 'pointer',
                      border: `1.5px solid ${draft.categoryId === cat.id ? 'var(--accent)' : 'var(--border)'}`,
                      background: draft.categoryId === cat.id ? 'var(--accent-soft)' : 'transparent',
                      color: draft.categoryId === cat.id ? 'var(--accent)' : 'var(--text2)',
                      fontSize: 13, fontWeight: 600, transition: 'all 0.15s',
                      display: 'flex', alignItems: 'center', gap: 5,
                    }}>
                    {cat.emoji && <span>{cat.emoji}</span>}
                    {cat.name}
                  </button>
                ))}
              </div>

              <textarea
                placeholder="Write something..."
                value={draft.content}
                onChange={e => {
                  setDraft({ ...draft, content: e.target.value });
                  e.target.style.height = 'auto';
                  e.target.style.height = `${Math.max(180, e.target.scrollHeight)}px`;
                }}
                style={{
                  width: '100%',
                  minHeight: 180,
                  height: 'auto',
                  border: 'none',
                  resize: 'none',
                  outline: 'none',
                  background: 'transparent',
                  color: 'var(--text)',
                  fontSize: 15,
                  lineHeight: 1.6,
                  fontFamily: 'inherit'
                }}
              />
              
              {video && (
                <div className={styles.composerPreview}>
                  <button className={styles.composerPreviewRemove} onClick={() => setVideo(null)}>×</button>
                  <video src={video.isExisting ? `${API}${video.url}` : URL.createObjectURL(video)} controls />
                </div>
              )}
              
              {images.length > 0 && (
                <div className={styles.composerPreviewGrid}>
                  {images.map((img, idx) => (
                    <div 
                      key={idx} 
                      className={styles.composerPreviewImage}
                      draggable
                      onDragStart={(e) => e.dataTransfer.setData('text/plain', idx)}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        const fromIdx = parseInt(e.dataTransfer.getData('text/plain'), 10);
                        if (fromIdx !== idx && !isNaN(fromIdx)) {
                          setImages(prev => {
                            const updated = [...prev];
                            const [moved] = updated.splice(fromIdx, 1);
                            updated.splice(idx, 0, moved);
                            return updated;
                          });
                        }
                      }}
                      style={{ cursor: 'grab' }}
                    >
                      <button className={styles.composerPreviewRemove} onClick={() => setImages(prev => prev.filter((_, i) => i !== idx))}>×</button>
                      <img src={typeof img === 'string' ? `${API}${img}` : URL.createObjectURL(img)} alt="Preview" draggable="false" />
                    </div>
                  ))}
                </div>
              )}
              
              <input 
                type="text" 
                className={styles.tagInput} 
                placeholder="Add tags (comma separated, e.g. react, design)"
                value={tagsInput}
                onChange={e => setTagsInput(e.target.value)}
                style={{ display: tagsInput || draft.content.length > 50 ? 'block' : 'none' }}
              />
              
              <input type="file" style={{ display: 'none' }} ref={fileInputRef} onChange={handleFileChange} multiple />
            </div>
            
            {/* Modal Footer */}
            <div style={{ padding: '16px 24px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, borderTop: '1px solid var(--border)', flexShrink: 0 }}>
              <div style={{ display: 'flex', gap: 14, color: 'var(--text3)', fontSize: 18, alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ cursor: 'pointer' }} onClick={() => { fileInputRef.current.accept = "image/*"; fileInputRef.current.click(); }} title="Attach">📎</span>
                <span style={{ cursor: 'pointer' }} title="Link">🔗</span>
                <span style={{ cursor: 'pointer' }} onClick={() => { fileInputRef.current.accept = "video/*"; fileInputRef.current.click(); }} title="Video">▶</span>
                <span style={{ cursor: 'pointer' }} title="Poll">📊</span>
                <span style={{ cursor: 'pointer' }} title="Emoji">😊</span>
                <span style={{ cursor: 'pointer', fontSize: 13, fontWeight: 700 }} title="GIF">GIF</span>
                
                {draft.categoryId && (() => {
                  const cat = categories.find(c => c.id === draft.categoryId);
                  return cat ? (
                    <div style={{ marginLeft: 6, padding: '5px 12px', borderRadius: 20, background: 'var(--accent-soft)', color: 'var(--accent)', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}>
                      {cat.emoji && <span>{cat.emoji}</span>}
                      {cat.name}
                    </div>
                  ) : null;
                })()}
              </div>
              
              <div style={{ display: 'flex', gap: 12 }}>
                <button
                  className="btn btn-primary" 
                  onClick={() => {
                    // Temporarily combine title and content for backend
                    if (draft.title) draft.content = `**${draft.title}**\n\n${draft.content}`;
                    submitPost();
                  }} 
                  disabled={!draft.content.trim() && !(draft.title || '').trim() || uploading}
                  style={{ background: (!draft.content.trim() && !(draft.title || '').trim()) ? 'var(--surface2)' : 'var(--accent)', color: (!draft.content.trim() && !(draft.title || '').trim()) ? 'var(--text3)' : 'white', border: 'none', fontWeight: 600, padding: '10px 24px' }}
                >
                  {uploading ? 'POSTING...' : 'POST'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>

      <div className={styles.sidebarColumn}>
        {/* Community Info Card */}
        <div className="card" style={{ marginBottom: 20, overflow: 'hidden' }}>
          <div style={{
            height: 100,
            background: community?.coverImageUrl
              ? `url(${API}${community.coverImageUrl}) center/cover no-repeat`
              : 'linear-gradient(45deg, #0f172a, #1e293b)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white'
          }}>
            {!community?.coverImageUrl && (
              <>
                <div style={{ fontSize: 16, fontWeight: 900, letterSpacing: 1, marginBottom: 2, color: '#38bdf8' }}>{(community?.name || 'Community').toUpperCase()}</div>
                <div style={{ fontSize: 12, fontWeight: 800 }}>WELCOME TO OUR COMMUNITY</div>
              </>
            )}
          </div>
          <div className="card-body">
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4, color: 'var(--text)' }}>{community?.name || 'Community'}</h3>
            <p className="text-muted" style={{ fontSize: 11, marginBottom: 12 }}>qeduvo.com/{community?.slug || 'url'}</p>
            <p style={{ fontSize: 13, lineHeight: 1.5, marginBottom: 16, color: 'var(--text2)' }}>
              {community?.description || 'Welcome! Discover resources and connect with members in our community.'}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
              <button 
                onClick={() => navigate(`/${slug}/classroom`)}
                style={{ 
                  fontSize: 12, 
                  color: 'var(--text2)', 
                  textDecoration: 'none', 
                  background: 'none', 
                  border: 'none', 
                  padding: 0, 
                  cursor: 'pointer', 
                  textAlign: 'left', 
                  font: 'inherit',
                  width: 'fit-content'
                }}
              >
                🔗 View Available Courses
              </button>
            </div>
            <div className="divider" style={{ margin: '16px 0' }} />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', textAlign: 'center', marginBottom: 16 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{membersCount}</div>
                <div style={{ fontSize: 10, color: 'var(--text3)' }}>Members</div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{onlineCount}</div>
                <div style={{ fontSize: 10, color: 'var(--text3)' }}>Online</div>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{adminsCount}</div>
                <div style={{ fontSize: 10, color: 'var(--text3)' }}>Admins</div>
              </div>
            </div>
             <div style={{ display: 'flex', gap: -5, justifyContent: 'center', marginBottom: 16 }}>
               {users.slice(0, 6).map((u, i) => (
                 <div key={u.id} style={{ marginLeft: i > 0 ? -8 : 0, zIndex: 6 - i }}>
                   <Avatar user={{ avatarEmoji: u.avatarEmoji, displayName: u.displayName }} size={24} />
                 </div>
               ))}
              </div>
           </div>
         </div>

        {/* Leaderboard Widget */}
        <div className="card">
          <div className="card-body">
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text2)', marginBottom: 16 }}>Leaderboard (30-day)</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {users.slice(0, 5).map((item, idx) => (
                <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{ width: 24, height: 24, borderRadius: '50%', background: idx === 0 ? '#fef08a' : idx === 1 ? '#e5e7eb' : idx === 2 ? '#fed7aa' : 'transparent', color: idx === 0 ? '#854d0e' : idx === 1 ? '#374151' : idx === 2 ? '#9a3412' : 'var(--text3)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700 }}>
                    {idx + 1}
                  </div>
                  <Avatar user={{ avatarEmoji: item.avatarEmoji, displayName: item.displayName }} size={24} />
                  <div style={{ fontSize: 13, color: 'var(--text)', flex: 1 }}>{item.displayName}</div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--accent)' }}>{item.totalXp} XP</div>
                </div>
              ))}
              {users.length === 0 && (
                <div style={{ fontSize: 12, color: 'var(--text3)', textAlign: 'center', padding: '10px 0' }}>
                  No members yet.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {gallery.open && gallery.post && (
        <div className={styles.galleryOverlay} onClick={() => setGallery({ open: false, post: null, currentIndex: 0 })}>
          <button className={styles.galleryClose} onClick={() => setGallery({ open: false, post: null, currentIndex: 0 })}>✕</button>
          
          <div className={`${styles.galleryImageContainer} ${!gallery.post.imageUrls?.length ? styles.noImage : ''}`} onClick={(e) => e.stopPropagation()}>
            {gallery.post.imageUrls?.length > 0 ? (
              <>
                {gallery.post.imageUrls.length > 1 && (
                  <button 
                    className={`${styles.galleryNav} ${styles.galleryPrev}`} 
                    onClick={(e) => {
                      e.stopPropagation();
                      setGallery(prev => ({ ...prev, currentIndex: (prev.currentIndex - 1 + prev.post.imageUrls.length) % prev.post.imageUrls.length }));
                    }}
                  >
                    ‹
                  </button>
                )}
                
                <img 
                  src={`${API}${gallery.post.imageUrls[gallery.currentIndex]}`} 
                  className={styles.galleryImage} 
                  alt="Gallery view" 
                />
                
                {gallery.post.imageUrls.length > 1 && (
                  <button 
                    className={`${styles.galleryNav} ${styles.galleryNext}`} 
                    onClick={(e) => {
                      e.stopPropagation();
                      setGallery(prev => ({ ...prev, currentIndex: (prev.currentIndex + 1) % prev.post.imageUrls.length }));
                    }}
                  >
                    ›
                  </button>
                )}
              </>
            ) : (
              <div style={{ color: '#fff', fontSize: '18px', textAlign: 'center', maxWidth: 600, padding: 40 }}>
                {gallery.post.content}
              </div>
            )}
          </div>
          
          <div className={styles.gallerySidebar} onClick={(e) => e.stopPropagation()}>
            <div className={styles.gallerySidebarHeader}>
              <div className={styles.postHeader} style={{ marginBottom: 0 }}>
                <Avatar user={{ avatar: gallery.post.authorAvatar, name: gallery.post.authorName }} />
                <div className={styles.postMeta}>
                  <div className={styles.authorName}>{gallery.post.authorName}</div>
                  <div className={styles.postTime}>{new Date(gallery.post.createdAt).toLocaleDateString()}</div>
                </div>
              </div>
            </div>
            
            <div className={styles.gallerySidebarScrollArea}>
              <div className={styles.gallerySidebarContent}>
                <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {gallery.post.content}
                </div>
              </div>
              
              <div className={styles.gallerySidebarComments}>
                {loadingComments ? (
                  <div style={{ textAlign: 'center', color: 'var(--text3)', marginTop: 20 }}>Loading comments...</div>
                ) : (comments[gallery.post.id] || []).length === 0 ? (
                  <div style={{ textAlign: 'center', color: 'var(--text3)', marginTop: 20 }}>No comments yet. Be the first!</div>
                ) : (
                  renderComments(buildCommentTree(comments[gallery.post.id] || []))
                )}
              </div>
            </div>
            
            <div className={styles.gallerySidebarInput} style={{ flexDirection: 'column' }}>
              {replyingTo && (
                <div style={{ fontSize: 12, color: 'var(--text3)', display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span>Replying to {replyingTo.name}</span>
                  <span style={{ cursor: 'pointer' }} onClick={() => setReplyingTo(null)}>✕</span>
                </div>
              )}
              {commentImage && (
                <div className={styles.commentImagePreview}>
                  <img src={URL.createObjectURL(commentImage)} alt="Preview" />
                  <button onClick={() => setCommentImage(null)}>✕</button>
                </div>
              )}
              <div style={{ display: 'flex', gap: 12, width: '100%', alignItems: 'center' }}>
                <Avatar user={{ avatar: user?.avatar_url || user?.avatarEmoji, name: user?.display_name || user?.displayName }} size={36} />
                <input type="file" style={{ display: 'none' }} ref={commentFileInputRef} accept="image/*" onChange={e => {
                  if (e.target.files[0]) setCommentImage(e.target.files[0]);
                }} />
                <button className="icon-btn" style={{ padding: '0 8px' }} onClick={() => commentFileInputRef.current?.click()}>
                  🖼️
                </button>
                <input 
                  type="text" 
                  className={styles.commentInput} 
                  placeholder="Write a comment..." 
                  value={commentInput}
                  onChange={e => setCommentInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') submitComment(gallery.post.id);
                  }}
                />
                <button
                  className="btn btn-primary"
                  style={{ 
                    width: '36px', 
                    height: '36px', 
                    borderRadius: '50%', 
                    padding: 0, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    flexShrink: 0 
                  }}
                  onClick={() => submitComment(gallery.post.id)}
                  disabled={!commentInput.trim() && !commentImage}
                  aria-label="Send"
                >
                  <Send size={16} />
                </button>
            </div>
          </div>
        </div>
      </div>
      )}

    </div>
  );
}
